/**
 * EditorialHomePage, The LWD Edit
 *
 * The LWD luxury editorial homepage. Feels like a high-end fashion/travel
 * magazine index: cinematic cover story, asymmetric feature grid, editorial
 * section headers, premium card treatments throughout.
 *
 * Layout rhythm:
 *   1. Magazine masthead (sticky), THE LWD EDIT · issue label · nav
 *   2. Cover Story, full-viewport cinematic hero
 *   3. Issue strip, season label + category nav
 *   4. This Issue, 2-col asymmetric feature grid
 *   5. Full-width editorial break (destinations / real-weddings)
 *   6. Also In This Issue, 3-col tight grid
 *   7. Latest Stories, 4-up compact grid
 *   8. Footer
 */

import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import SearchIdentity from '../../seo/SearchIdentity.jsx';
import {
  DARK, GOLD, CREAM, FD, FU, FS,
  EDITORIAL_CATEGORIES, resolveTagCategory,
} from './editorialTheme.js';
import { supabase } from '../../lib/supabaseClient.js';
import { useEditorialLights, C_VARS } from './useEditorialLights.js';
import { usePrerenderReady } from '../../utils/prerenderReady';
import DirectoryNav, { DIRECTORY_NAV_HEIGHT } from './DirectoryNav.jsx';
import CollectionLedCategory from '../Magazine/CollectionLedCategory.jsx';
import { isPubliclyRoutable } from '../../utils/publicRoutability.js';
import { imgUrl } from '../../utils/imgUrl';

// C resolves through CSS variables (set on <html> by useEditorialLights),
// so every subcomponent that already references C.bg / C.text / etc.
// re-themes automatically when the lights toggle fires, no prop drilling.
const C = C_VARS;

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function getIssueLabel() {
  const d = new Date();
  const y = d.getFullYear();
  return d.getMonth() < 6 ? `Spring · Summer ${y}` : `Autumn · Winter ${y}`;
}

function slugifyLabel(str) {
  return (str || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

function categoryAccent(post) {
  const labelSlug = slugifyLabel(post.category_label);
  const tagCat    = (post.tags || []).map(resolveTagCategory).find(Boolean);
  const id        = EDITORIAL_CATEGORIES.find(c => c.id === labelSlug)?.id || tagCat;
  const found     = EDITORIAL_CATEGORIES.find(c => c.id === id);
  return found ? found.accent : GOLD;
}

function postCategoryDisplay(post) {
  return post.category_label || (post.tags && post.tags[0]) || '';
}

// ── Masthead ──────────────────────────────────────────────────────────────────
// Exported so EditorialArticlePage and any future Edit views can reuse the
// same canonical header, LWD wordmark · THE LWD EDIT + issue label · ← Directory,
// with the integrated category nav strip below. One header, one identity.

export function Masthead({ activeId, onNavigateHome, onNavigateCategory, isDark, onToggleLights }) {
  // compact > 140 → THE LWD EDIT wordmark has scrolled off; category strip is
  // the only visible header element, glass bg and gold border kick in.
  const [compact,  setCompact]  = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const issueLabel = getIssueLabel();

  useEffect(() => {
    const onScroll = () => setCompact(window.scrollY > 140);
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Masthead wordmark is static, it scrolls away like a magazine cover page.
  // The category strip is a SIBLING of the header (not a child) so that
  // position: sticky is not bounded by the header's height and works for
  // the full page scroll.
  return (
    <>
    <header style={{ background: C.bg }}>
      <div style={{
        display:         isMobile ? 'flex' : 'grid',
        gridTemplateColumns: isMobile ? undefined : '1fr auto 1fr',
        justifyContent:  isMobile ? 'center' : undefined,
        alignItems:      'center',
        padding:         '0 clamp(24px, 6vw, 80px)',
        height:          isMobile ? 72 : (compact ? 64 : 108),
        gap:             16,
        transition:      'height 0.35s ease',
      }}>
        {/* Left column: empty spacer, hidden on mobile */}
        {!isMobile && <div />}

        {/* Centre: THE LWD EDIT, clicks back to /the-edit. This is the
            primary visual identity: serif, expressive, dominant. */}
        <button
          onClick={() => onNavigateCategory && onNavigateCategory(null)}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          <span style={{
            fontFamily:    FD,
            fontSize:      compact ? 'clamp(18px, 1.9vw, 24px)' : 'clamp(22px, 2.4vw, 32px)',
            fontWeight:    400,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color:         C.text,
            lineHeight:    1,
            transition:    'font-size 0.35s ease',
          }}>
            The LWD Edit
          </span>
          {/* Parent-brand line, sits below The LWD Edit wordmark */}
          <span style={{
            fontFamily:    FU,
            fontSize:      compact ? 7 : 8,
            fontWeight:    600,
            letterSpacing: '0.42em',
            textTransform: 'uppercase',
            color:         GOLD,
            lineHeight:    1,
            opacity:       compact ? 0 : 0.85,
            maxHeight:     compact ? 0 : 14,
            overflow:      'hidden',
            transition:    'opacity 0.3s ease, max-height 0.35s ease',
            marginTop:     compact ? 0 : 6,
          }}>
            Luxury Wedding Directory
          </span>
        </button>

        {/* Right: Lights toggle (article pages only) + Directory link.
            The toggle is intentionally styled to read as a quiet control,
            NOT a product-UI button: no box, no border, no focus ring,
            sized to match the Directory link's visual weight (same gold,
            same opacity-fade hover, same rhythm). It's there when you
            need it, invisible otherwise. */}
        {/* Mobile lights toggle, absolute right so it doesn't displace the centred wordmark */}
        {isMobile && onToggleLights && (
          <button
            onClick={onToggleLights}
            title={isDark ? 'Lights on' : 'Lights off'}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            style={{
              position: 'absolute', right: 'clamp(20px, 5vw, 32px)',
              fontFamily: FU, fontSize: 16, lineHeight: 1,
              color: GOLD, background: 'none', border: 'none',
              outline: 'none', cursor: 'pointer', padding: 4,
              display: 'inline-flex', alignItems: 'center',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.65'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            {isDark ? '☀' : '☾'}
          </button>
        )}

        {/* Right controls, desktop only */}
        {!isMobile && <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 18 }}>
          {onToggleLights && (
            <button
              onClick={onToggleLights}
              title={isDark ? 'Lights off (dark)' : 'Lights on (light)'}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                fontFamily:  FU,
                fontSize:    13,            // size of the glyph itself, like Directory's caps
                lineHeight:  1,
                color:       GOLD,
                background:  'none',
                border:      'none',
                outline:     'none',         // suppress browser's blue focus box
                cursor:      'pointer',
                padding:     0,
                display:     'inline-flex',
                alignItems:  'center',
                transition:  'opacity 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.65'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              onFocus={e => { e.currentTarget.style.boxShadow = 'none'; }}
            >
              {isDark ? '☀' : '☾'}
            </button>
          )}
          {/* Directory mark, editorial folio detail, not a back button.
              Diamond ornament before the label (same vocabulary as the LWD
              masthead lockup, the H ornament, and the Apply for Inclusion
              CTA). Sits at 65% opacity so it recedes from the masthead's
              voice; rises to 100% on hover/focus while a 1px gold rule
              draws from the centre outward beneath the label. Same
              interaction language as the H ornament, the page reads as
              one design family.
              The visible label is just "Directory"; the aria-label
              spells out "Return to directory" so assistive tech still
              announces the action clearly. */}
          {(() => {
            const reveal = (e) => {
              e.currentTarget.style.opacity = '1';
              const rule = e.currentTarget.querySelector('[data-dir-rule]');
              if (rule) rule.style.transform = 'scaleX(1)';
            };
            const conceal = (e) => {
              e.currentTarget.style.opacity = '0.65';
              const rule = e.currentTarget.querySelector('[data-dir-rule]');
              if (rule) rule.style.transform = 'scaleX(0)';
            };
            return (
              <button
                onClick={onNavigateHome}
                aria-label="Return to directory"
                style={{
                  fontFamily:    FU,
                  fontSize:      9,
                  fontWeight:    500,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color:         GOLD,
                  opacity:       0.65,
                  background:    'none',
                  border:        'none',
                  outline:       'none',
                  cursor:        'pointer',
                  padding:       '4px 0',
                  display:       'inline-flex',
                  alignItems:    'center',
                  gap:           10,
                  transition:    'opacity 0.25s ease',
                }}
                onMouseEnter={reveal}
                onMouseLeave={conceal}
                onFocus={reveal}
                onBlur={conceal}
              >
                {/* Diamond ornament, running-head anchor, ornament before
                    the label. Reads as a folio mark, not a "next step". */}
                <span aria-hidden style={{
                  width: 4, height: 4,
                  background: GOLD,
                  transform: 'rotate(45deg)',
                  flexShrink: 0,
                }} />
                <span style={{ position: 'relative' }}>
                  Directory
                  {/* Hairline gold rule that draws from centre outward on
                      hover/focus, matches the H ornament's reveal. */}
                  <span
                    aria-hidden
                    data-dir-rule
                    style={{
                      position:        'absolute',
                      left:            -2,
                      right:           -2,
                      bottom:          -3,
                      height:          1,
                      background:      GOLD,
                      transform:       'scaleX(0)',
                      transformOrigin: 'center',
                      transition:      'transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1)',
                    }}
                  />
                </span>
              </button>
            );
          })()}
        </div>}
      </div>
    </header>

    {/* Category nav strip, sibling of <header>, not a child.
        position: sticky on a child is bounded by the parent's height;
        as a sibling it sticks correctly for the full page scroll.
        Gets cream glass bg once the masthead wordmark has scrolled off.
        Shown on all screen sizes, mobile gets a horizontally scrollable
        strip so every category is reachable with a swipe. */}
    <nav style={{
        position:             'sticky',
        // DirectoryNav is 52px on mobile, DIRECTORY_NAV_HEIGHT (34px) on desktop
        top:                  isMobile ? 52 : DIRECTORY_NAV_HEIGHT,
        zIndex:               40,
        display:              'flex',
        alignItems:           'center',
        gap:                  isMobile ? 'clamp(20px, 5vw, 32px)' : 'clamp(20px, 2.5vw, 40px)',
        padding:              isMobile ? '0 clamp(16px, 4vw, 24px)' : '0 clamp(24px, 6vw, 80px)',
        height:               isMobile ? 44 : (compact ? 32 : 40),
        // Separator from THE LWD EDIT block above, hidden when compact because
        // DirectoryNav's own border serves as the top edge of the sticky bar.
        borderTop:            (compact || isMobile) ? 'none' : `1px solid ${C.border}`,
        background:           (compact || isMobile)
                                ? (isDark ? 'rgba(11,9,6,0.94)' : 'rgba(247,243,236,0.90)')
                                : C.bg,
        backdropFilter:       (compact || isMobile) ? 'blur(14px) saturate(1.08)' : 'none',
        WebkitBackdropFilter: (compact || isMobile) ? 'blur(14px) saturate(1.08)' : 'none',
        borderBottom:         (compact || isMobile)
                                ? '1px solid rgba(211,154,33,0.20)'
                                : `1px solid ${C.border}`,
        overflowX:            'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth:       'none',
        transition:           'height 0.35s ease, background 0.3s ease, border-color 0.3s ease, backdrop-filter 0.4s',
      }}>
        {/* Active state, refined editorial marker, not a UI button:
            text colour stays at C.text (full-strength cream) for the active
            label so the eye lands on it, while inactive items rest at
            C.body. The marker is a 1px gold underline floating ~6px below
            the baseline (extra padding-bottom + bottom-positioned border),
            so it reads as an editorial selection cue rather than a
            highlighted filter chip. The per-category accent now appears
            only on hover, not on the resting active state. */}
        <button
          onClick={() => onNavigateCategory && onNavigateCategory(null)}
          style={{
            position:      'relative',
            fontFamily:    FU,
            fontSize:      8,
            letterSpacing: '0.30em',
            textTransform: 'uppercase',
            color:         !activeId ? C.text : C.body,
            background:    'none',
            border:        'none',
            cursor:        'pointer',
            padding:       '0 0 8px',
            flexShrink:    0,
            transition:    'color 0.2s',
          }}
        >
          All
          {!activeId && (
            <span style={{
              position: 'absolute',
              left: '50%', bottom: 0,
              transform: 'translateX(-50%)',
              width: 18, height: 1,
              background: GOLD,
              transition: 'width 0.3s ease',
            }} />
          )}
        </button>
        {EDITORIAL_CATEGORIES.map(cat => {
          const isActive = activeId === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => onNavigateCategory(cat.id)}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = cat.accent; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = C.body; }}
              style={{
                position:      'relative',
                fontFamily:    FU,
                fontSize:      8,
                letterSpacing: '0.26em',
                textTransform: 'uppercase',
                color:         isActive ? C.text : C.body,
                background:    'none',
                border:        'none',
                cursor:        'pointer',
                padding:       '0 0 8px',
                flexShrink:    0,
                transition:    'color 0.2s',
              }}
            >
              {cat.label}
              {isActive && (
                <span style={{
                  position: 'absolute',
                  left: '50%', bottom: 0,
                  transform: 'translateX(-50%)',
                  width: 18, height: 1,
                  background: GOLD,
                }} />
              )}
            </button>
          );
        })}

        {/* Far-right issue label, desktop only; on mobile it would
            push the scrollable category list off screen. */}
        {!isMobile && <span style={{
          marginLeft: 'auto',
          fontFamily: FU,
          fontSize: 8,
          letterSpacing: '0.30em',
          textTransform: 'uppercase',
          color: GOLD,
          paddingBottom: 8,
          flexShrink: 0,
          whiteSpace: 'nowrap',
        }}>
          {issueLabel}
        </span>}
      </nav>
    </>
  );
}

// ── CoverStory, full-viewport cinematic hero ─────────────────────────────────

function CoverStory({ post, onNavigateArticle }) {
  const [loaded, setLoaded] = useState(false);
  const [hovered, setHovered] = useState(false);
  if (!post) return null;

  const img    = post.cover_image || '';
  const title  = post.title || '';
  const stan   = post.standfirst || post.excerpt || '';
  const cat    = postCategoryDisplay(post);
  const accent = categoryAccent(post);

  return (
    <section
      onClick={() => onNavigateArticle(post.slug)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        width:    '100%',
        height:   'clamp(580px, 92svh, 1000px)',
        overflow: 'hidden',
        cursor:   'pointer',
        // Hero uses the DARK palette regardless of the page's lights mode.
        // The cover is a cinematic editorial moment, a photograph with
        // overlay text, and that vocabulary requires a dark background
        // and light type, the way Vogue / Cereal / The Gentlewoman handle
        // their covers. Theme-following text would invert to dark in light
        // mode and disappear against the photograph.
        background: DARK.bg,
      }}
    >
      {/* Background image */}
      {img && (
        <img
          src={imgUrl(img, { width: 800 })}
          alt={title}
          onLoad={() => setLoaded(true)}
          style={{
            position:       'absolute',
            inset:          0,
            width:          '100%',
            height:         '100%',
            objectFit:      'cover',
            objectPosition: 'center 30%',
            opacity:        loaded ? 1 : 0,
            transform:      hovered ? 'scale(1.025)' : 'scale(1)',
            transition:     'opacity 1s ease, transform 8s cubic-bezier(0.25,0.46,0.45,0.94)',
          }}
        />
      )}

      {/* Deep gradient, editorial black fade. Uses DARK.bg (not theme C.bg)
          so the gradient stays dark in light mode too, cinematic hero
          treatment. */}
      <div style={{
        position:   'absolute',
        inset:      0,
        background: `
          linear-gradient(to top, ${DARK.bg} 0%, ${DARK.bg}e6 12%, ${DARK.bg}99 28%, ${DARK.bg}44 48%, transparent 65%),
          linear-gradient(to right, ${DARK.bg}cc 0%, ${DARK.bg}66 30%, transparent 60%)
        `,
      }} />

      {/* "Cover Story" label, top left */}
      <div style={{
        position:  'absolute',
        top:       'clamp(32px, 5vw, 56px)',
        left:      'clamp(32px, 6vw, 96px)',
        display:   'flex',
        flexDirection: 'column',
        gap:       8,
      }}>
        <span style={{
          fontFamily:    FU,
          fontSize:      8,
          letterSpacing: '0.36em',
          textTransform: 'uppercase',
          color:         GOLD,
          display:       'flex',
          alignItems:    'center',
          gap:           8,
        }}>
          <span style={{ width: 20, height: 1, background: GOLD, display: 'inline-block' }} />
          Cover Story
        </span>
        {cat && (
          <span style={{
            fontFamily:    FU,
            fontSize:      8,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color:         accent,
          }}>
            {cat}
          </span>
        )}
      </div>

      {/* Issue label, top right (cream regardless of page theme) */}
      <div style={{
        position:   'absolute',
        top:        'clamp(32px, 5vw, 56px)',
        right:      'clamp(32px, 6vw, 96px)',
        fontFamily: FU,
        fontSize:   8,
        letterSpacing: '0.28em',
        textTransform: 'uppercase',
        color:      DARK.subtle,
        textAlign:  'right',
      }}>
        {getIssueLabel()}
      </div>

      {/* Content, bottom. Title + standfirst use DARK tokens (cream)
          regardless of the page's lights mode, so the text remains legible
          on the dark gradient + photograph at all times. */}
      <div style={{
        position:  'absolute',
        bottom:    0,
        left:      0,
        right:     0,
        padding:   'clamp(32px, 6vw, 80px) clamp(32px, 6vw, 96px)',
        maxWidth:  940,
        display:   'flex',
        flexDirection: 'column',
        gap:       20,
      }}>
        <h1 style={{
          fontFamily:    FD,
          fontWeight:    400,
          fontSize:      'clamp(40px, 6vw, 88px)',
          lineHeight:    1.04,
          color:         DARK.text,
          margin:        0,
          letterSpacing: '-0.02em',
        }}>
          {title}
        </h1>

        {stan && (
          <p style={{
            fontFamily: FS,
            fontSize:   'clamp(15px, 1.5vw, 19px)',
            lineHeight: 1.65,
            color:      DARK.body,
            margin:     0,
            maxWidth:   580,
          }}>
            {stan}
          </p>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
          <div style={{ width: 28, height: 1, background: GOLD }} />
          <span style={{
            fontFamily:    FU,
            fontSize:      9,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color:         GOLD,
          }}>
            Read the story
          </span>
        </div>
      </div>

      {/* Bottom gold rule */}
      <div style={{
        position:   'absolute',
        bottom:     0,
        left:       0,
        right:      0,
        height:     1,
        background: `linear-gradient(to right, ${GOLD}00, ${GOLD}88, ${GOLD}00)`,
      }} />
    </section>
  );
}

// ── SectionHeader ─────────────────────────────────────────────────────────────

function SectionHeader({ label, sub }) {
  return (
    <div style={{
      display:        'flex',
      alignItems:     'baseline',
      gap:            20,
      marginBottom:   'clamp(28px, 3vw, 44px)',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <h2 style={{
          fontFamily:    FD,
          fontWeight:    400,
          fontSize:      'clamp(24px, 2.4vw, 36px)',
          color:         C.text,
          margin:        0,
          letterSpacing: '-0.01em',
          lineHeight:    1,
        }}>
          {label}
        </h2>
        {sub && (
          <span style={{
            fontFamily:    FU,
            fontSize:      8,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color:         GOLD,
          }}>
            {sub}
          </span>
        )}
      </div>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(to right, ${C.border2}, transparent)` }} />
    </div>
  );
}

// ── FeatureCard, large editorial card ───────────────────────────────────────

function FeatureCard({ post, onNavigateArticle, size = 'md', showExcerpt = true }) {
  const [hovered, setHovered] = useState(false);
  if (!post) return null;

  const img    = post.cover_image || '';
  const title  = post.title || '';
  const cat    = postCategoryDisplay(post);
  const accent = categoryAccent(post);

  const imgH = { sm: 200, md: 300, lg: 440, xl: 560 }[size] || 300;

  return (
    <article
      onClick={() => onNavigateArticle(post.slug)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor:        'pointer',
        display:       'flex',
        flexDirection: 'column',
        gap:           16,
      }}
    >
      {/* Image */}
      <div style={{
        position:   'relative',
        height:     imgH,
        overflow:   'hidden',
        background: C.card,
        flexShrink: 0,
      }}>
        {img && (
          <img
            src={imgUrl(img, { width: 800 })}
            alt={title}
            style={{
              width:      '100%',
              height:     '100%',
              objectFit:  'cover',
              transform:  hovered ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            }}
          />
        )}
        {/* Gradient overlay */}
        <div style={{
          position:   'absolute',
          inset:      0,
          background: `linear-gradient(to top, ${C.bg}88 0%, transparent 50%)`,
          opacity:    hovered ? 1 : 0.5,
          transition: 'opacity 0.4s',
        }} />
        {/* Category */}
        {cat && (
          <div style={{
            position:      'absolute',
            top:           14,
            left:          14,
            fontFamily:    FU,
            fontSize:      7,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color:         accent,
            background:    `${C.bg}cc`,
            padding:       '3px 9px',
          }}>
            {cat}
          </div>
        )}
      </div>

      {/* Text block */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {/* Gold accent line */}
        <div style={{ width: 24, height: 1, background: accent, opacity: 0.8 }} />

        <h3 style={{
          fontFamily:    FD,
          fontWeight:    400,
          fontSize:      size === 'xl' || size === 'lg'
            ? 'clamp(20px, 2vw, 28px)'
            : 'clamp(16px, 1.6vw, 20px)',
          lineHeight:    1.2,
          color:         hovered ? GOLD : C.text,
          margin:        0,
          letterSpacing: '-0.01em',
          transition:    'color 0.25s',
        }}>
          {title}
        </h3>

        {showExcerpt && post.standfirst && size !== 'sm' && (
          <p style={{
            fontFamily:         FS,
            fontSize:           13,
            lineHeight:         1.65,
            color:              C.muted,
            margin:             0,
            display:            '-webkit-box',
            WebkitLineClamp:    2,
            WebkitBoxOrient:    'vertical',
            overflow:           'hidden',
          }}>
            {post.standfirst}
          </p>
        )}

        <div style={{
          display:    'flex',
          alignItems: 'center',
          gap:        8,
          marginTop:  2,
        }}>
          <span style={{
            fontFamily:    FU,
            fontSize:      8,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color:         C.subtle,
          }}>
            {fmtDate(post.published_at || post.created_at)}
          </span>
          {post.reading_time && (
            <>
              <span style={{ color: C.subtle, fontSize: 8 }}>·</span>
              <span style={{
                fontFamily:    FU,
                fontSize:      8,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color:         C.subtle,
              }}>
                {post.reading_time} min read
              </span>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

// ── FullWidthEditorial, wide cinematic break ─────────────────────────────────

function FullWidthEditorial({ post, onNavigateArticle, sectionLabel }) {
  const [hovered, setHovered] = useState(false);
  if (!post) return null;

  const img    = post.cover_image || '';
  const title  = post.title || '';
  const stan   = post.standfirst || post.excerpt || '';
  const accent = categoryAccent(post);

  return (
    <section
      onClick={() => onNavigateArticle(post.slug)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position:   'relative',
        width:      '100%',
        height:     'clamp(340px, 48vw, 640px)',
        overflow:   'hidden',
        cursor:     'pointer',
        background: C.surface,
      }}
    >
      {img && (
        <img
          src={imgUrl(img, { width: 800 })}
          alt={title}
          style={{
            width:          '100%',
            height:         '100%',
            objectFit:      'cover',
            objectPosition: 'center 40%',
            transform:      hovered ? 'scale(1.03)' : 'scale(1)',
            transition:     'transform 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}
        />
      )}
      <div style={{
        position:   'absolute',
        inset:      0,
        background: `linear-gradient(105deg, ${C.bg}ee 0%, ${C.bg}aa 35%, transparent 62%)`,
      }} />

      <div style={{
        position:      'absolute',
        top:           '50%',
        left:          'clamp(32px, 8vw, 120px)',
        transform:     'translateY(-50%)',
        maxWidth:      540,
        display:       'flex',
        flexDirection: 'column',
        gap:           18,
      }}>
        {sectionLabel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 20, height: 1, background: accent }} />
            <span style={{
              fontFamily:    FU,
              fontSize:      8,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              color:         accent,
            }}>
              {sectionLabel}
            </span>
          </div>
        )}
        <h2 style={{
          fontFamily:    FD,
          fontWeight:    400,
          fontSize:      'clamp(28px, 3.8vw, 56px)',
          lineHeight:    1.08,
          color:         C.text,
          margin:        0,
          letterSpacing: '-0.02em',
        }}>
          {title}
        </h2>
        {stan && (
          <p style={{
            fontFamily: FS,
            fontSize:   'clamp(14px, 1.3vw, 16px)',
            lineHeight: 1.7,
            color:      C.body,
            margin:     0,
          }}>
            {stan}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
          <div style={{ width: 20, height: 1, background: GOLD }} />
          <span style={{
            fontFamily:    FU,
            fontSize:      9,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color:         GOLD,
          }}>
            Read
          </span>
        </div>
      </div>
    </section>
  );
}

// ── CompactCard, 4-up latest grid ────────────────────────────────────────────

function CompactCard({ post, onNavigateArticle }) {
  const [hovered, setHovered] = useState(false);
  if (!post) return null;

  const img    = post.cover_image || '';
  const title  = post.title || '';
  const cat    = postCategoryDisplay(post);
  const accent = categoryAccent(post);

  return (
    <article
      onClick={() => onNavigateArticle(post.slug)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor:        'pointer',
        display:       'flex',
        flexDirection: 'column',
        gap:           10,
      }}
    >
      <div style={{
        position:   'relative',
        height:     'clamp(160px, 18vw, 240px)',
        overflow:   'hidden',
        background: C.card,
      }}>
        {img && (
          <img
            src={imgUrl(img, { width: 800 })}
            alt={title}
            style={{
              width:      '100%',
              height:     '100%',
              objectFit:  'cover',
              transform:  hovered ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.7s ease',
            }}
          />
        )}
        {cat && (
          <div style={{
            position:      'absolute',
            top:           10,
            left:          10,
            fontFamily:    FU,
            fontSize:      7,
            letterSpacing: '0.26em',
            textTransform: 'uppercase',
            color:         accent,
            background:    `${C.bg}cc`,
            padding:       '2px 7px',
          }}>
            {cat}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ width: 16, height: 1, background: accent, opacity: 0.7 }} />
        <h4 style={{
          fontFamily:    FD,
          fontWeight:    400,
          fontSize:      'clamp(14px, 1.3vw, 17px)',
          lineHeight:    1.25,
          color:         hovered ? GOLD : C.text,
          margin:        0,
          transition:    'color 0.2s',
        }}>
          {title}
        </h4>
        <span style={{
          fontFamily:    FU,
          fontSize:      8,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color:         C.subtle,
        }}>
          {fmtDate(post.published_at || post.created_at)}
        </span>
      </div>
    </article>
  );
}

// ── HorizontalArticleRow, for list-style latest ─────────────────────────────

function HorizontalRow({ post, onNavigateArticle, border = true }) {
  const [hovered, setHovered] = useState(false);
  if (!post) return null;

  const img    = post.cover_image || '';
  const title  = post.title || '';
  const cat    = postCategoryDisplay(post);
  const accent = categoryAccent(post);

  return (
    <article
      onClick={() => onNavigateArticle(post.slug)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        cursor:      'pointer',
        display:     'flex',
        gap:         20,
        alignItems:  'flex-start',
        padding:     border ? '22px 0' : '22px 0 0',
        borderTop:   border ? `1px solid ${C.border}` : 'none',
      }}
    >
      {/* Thumbnail */}
      {img && (
        <div style={{
          width:      100,
          height:     72,
          flexShrink: 0,
          overflow:   'hidden',
          background: C.card,
        }}>
          <img
            src={imgUrl(img, { width: 800 })}
            alt={title}
            style={{
              width:      '100%',
              height:     '100%',
              objectFit:  'cover',
              transform:  hovered ? 'scale(1.07)' : 'scale(1)',
              transition: 'transform 0.6s ease',
            }}
          />
        </div>
      )}

      {/* Text */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        {cat && (
          <span style={{
            fontFamily:    FU,
            fontSize:      7,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color:         accent,
          }}>
            {cat}
          </span>
        )}
        <h4 style={{
          fontFamily:    FD,
          fontWeight:    400,
          fontSize:      'clamp(14px, 1.3vw, 17px)',
          lineHeight:    1.25,
          color:         hovered ? GOLD : C.text,
          margin:        0,
          transition:    'color 0.2s',
          overflow:      'hidden',
          display:       '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}>
          {title}
        </h4>
        <span style={{
          fontFamily:    FU,
          fontSize:      8,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color:         C.subtle,
        }}>
          {fmtDate(post.published_at || post.created_at)}
        </span>
      </div>
    </article>
  );
}

// ── EditorialHomePage (main export) ──────────────────────────────────────────

export default function EditorialHomePage({
  categoryId = null,
  onNavigateArticle,
  onNavigateCategory,
  onNavigateHome,
  footerNav,
}) {
  const [posts,   setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [catRecord, setCatRecord] = useState(null); // magazine_categories row (hero + manifesto) for category views
  const [chapterImages, setChapterImages] = useState({}); // slug -> hero image, for the "Continue Exploring" cards
  const [collectionsData, setCollectionsData] = useState(null); // real editorial_collections rows for this chapter
  // Prerender readiness (P1b follow-up, 2026-07-24): the cover-story H1 and the generated
  // title/description contract come from posts[0], gated by loading. catRecord/
  // chapterImages/collectionsData feed secondary decorative sections (hero manifesto,
  // "Continue Exploring" cards) that aren't part of the SEO-critical contract, so they
  // deliberately don't gate this — waiting on all three would meaningfully complicate the
  // signal for content that isn't what this fix is protecting.
  usePrerenderReady(!loading);
  // Shared lights state, synced with article pages via localStorage + custom
  // event so toggling here re-themes any other open Edit route immediately.
  const [isDark, , toggleLights] = useEditorialLights();

  useEffect(() => {
    async function load() {
      setLoading(true);
      let q = supabase
        .from('magazine_posts')
        .select(`
          id, slug, title, standfirst, excerpt, cover_image,
          category_slug, category_label, tags, secondary_categories,
          template_type, featured_label,
          editors_choice, editorial_priority, published_at, created_at,
          reading_time, location_tags, style_tags
        `)
        // PUBLISHED ONLY. This filter was absent, so the public Edit hubs rendered
        // every draft alongside every published article: 104 of 429 posts, 59 of them
        // in Our Venues.
        //
        // That is a commercial privacy problem before it is an SEO one. Cold-call
        // prospects get an Edit article written during the build and left unpublished
        // until they sign. Capovaticano Resort and The Athenaeum are both draft
        // listings with private previews and no agreement, and their article titles
        // and cover images were being advertised on a public category page. The
        // article itself correctly 404'd, which made it look handled; the hub was
        // still announcing that it exists.
        //
        // A public hub shows published content. Drafts belong in the studio.
        .eq('published', true)
        .order('editorial_priority', { ascending: true, nullsFirst: false })
        .order('published_at', { ascending: false })
        // Category views drive the collection-led experience and need the full
        // set (collections/moods query across all of them); the home stays lean.
        .limit(categoryId ? 300 : 24);

      if (categoryId) {
        q = q.or(`category_slug.eq.${categoryId},tags.cs.{"${categoryId}"}`);
      }

      const { data, error } = await q;
      if (!error && data) setPosts(data);
      setLoading(false);
    }
    load();

    // Category record (hero image + editorial manifesto) for the section hero
    if (categoryId) {
      supabase.from('magazine_categories')
        .select('slug, name, description, hero_image')
        .eq('slug', categoryId).maybeSingle()
        .then(({ data }) => setCatRecord(data || null));

      // All chapter hero images for the "Continue Exploring" cards. Any chapter
      // missing a hero_image falls back to its best (highest-priority) article cover.
      const CHAP = ['our-venues', 'our-creatives', 'real-weddings', 'destinations', 'perspectives', 'fashion'];
      (async () => {
        const { data: cats } = await supabase.from('magazine_categories')
          .select('slug, hero_image').in('slug', CHAP);
        const map = {};
        (cats || []).forEach(c => { if (c.hero_image) map[c.slug] = c.hero_image; });
        const missing = CHAP.filter(s => !map[s]);
        if (missing.length) {
          const { data: fb } = await supabase.from('magazine_posts')
            .select('category_slug, cover_image, editorial_priority')
            .in('category_slug', missing).not('cover_image', 'is', null)
            .order('editorial_priority', { ascending: true, nullsFirst: false });
          (fb || []).forEach(p => { if (!map[p.category_slug]) map[p.category_slug] = p.cover_image; });
        }
        setChapterImages(map);
      })();

      // Real editorial collections for this chapter (Featured Collections + landing pages)
      supabase.from('editorial_collections')
        .select('slug, title, subtitle, editorial_intro, hero_image_url, featured_listings, filter_rules, published')
        .eq('published', true)
        .filter('filter_rules->>category_slug', 'eq', categoryId)
        .then(({ data }) => setCollectionsData(data || []));
    } else {
      setCatRecord(null);
    }
  }, [categoryId]);

  // Slot assignments, deliberate editorial hierarchy
  const cover   = posts[0] || null;         // hero / cover story
  const feat1   = posts[1] || null;         // left feature
  const feat2   = posts[2] || null;         // right feature (tall)
  const feat3   = posts[3] || null;         // right feature (short)
  const breakA  = posts[4] || null;         // full-width editorial break
  const grid3   = posts.slice(5, 8);        // 3-col "also in this issue"
  const latest  = posts.slice(8, 16);       // 4-up compact grid

  // Label precedence: the curated list, then the label the DB already stores on
  // the posts, then a title-cased slug. The old code fell straight back to the
  // raw categoryId, so any category missing from EDITORIAL_CATEGORIES rendered
  // its slug as the page's <h1> ("editorial", lowercase, on a hub with 23
  // articles). Adding the missing entry fixes today's case; this fallback stops
  // the next omission reaching the page at all.
  const catLabel = categoryId
    ? (EDITORIAL_CATEGORIES.find(c => c.id === categoryId)?.label
       || posts.find(p => p.category_slug === categoryId)?.category_label
       || String(categoryId).split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '))
    : null;

  const issueLabel = getIssueLabel();

  return (
    <div style={{ background: C.bg, minHeight: '100svh', color: C.text }}>
      {/* /lwd-edit/category/{cat} carries its own identity (not in INJECT_IDENTITY_PATHS,
          unlike bare /lwd-edit) — the identity engine is the single source of truth for
          title/canonical/robots, same pattern as RegionCategoryPage. Fixed 2026-08-02:
          this route had never been reachable before (Project 3, Class 5), so its own
          hand-rolled Helmet title/canonical had drifted from what the engine expects and
          carried no canonical tag at all — only ever caught once the route went live. */}
      {categoryId ? (
        <SearchIdentity path={`/lwd-edit/category/${categoryId}`} />
      ) : (
        <Helmet>
          <title>{`The LWD Edit, ${issueLabel} · Luxury Wedding Directory`}</title>
          <meta name="description" content="Cinematic wedding stories, destination inspiration and editorial features from Luxury Wedding Directory." />
          <meta property="og:type" content="website" />
        </Helmet>
      )}

      {/* ── Directory layer (commerce / wayfinding, sticky always) ── */}
      <DirectoryNav />

      {/* ── Edit Masthead (editorial identity, sticky beneath DirectoryNav) ── */}
      <Masthead
        activeId={categoryId}
        onNavigateHome={onNavigateHome}
        onNavigateCategory={onNavigateCategory}
        isDark={isDark}
        onToggleLights={toggleLights}
      />

      {loading ? (
        /* Loading state */
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          minHeight:      '70svh',
          flexDirection:  'column',
          gap:            16,
        }}>
          <div style={{ width: 1, height: 48, background: `linear-gradient(to bottom, ${GOLD}00, ${GOLD})` }} />
          <span style={{
            fontFamily:    FU,
            fontSize:      9,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color:         C.subtle,
          }}>
            Loading
          </span>
        </div>
      ) : posts.length === 0 ? (
        /* Empty state */
        <div style={{
          display:        'flex',
          flexDirection:  'column',
          alignItems:     'center',
          justifyContent: 'center',
          minHeight:      '60svh',
          gap:            20,
          padding:        '0 24px',
          textAlign:      'center',
        }}>
          <div style={{ width: 1, height: 40, background: `linear-gradient(to bottom, ${GOLD}00, ${GOLD})` }} />
          <p style={{ fontFamily: FD, fontSize: 28, color: C.muted, margin: 0 }}>
            Stories coming soon
          </p>
          <p style={{
            fontFamily:    FU,
            fontSize:      10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color:         C.subtle,
            margin:        0,
          }}>
            {catLabel
              ? `No published ${catLabel} articles yet`
              : 'The first issue is being prepared'}
          </p>
          <div style={{ width: 1, height: 40, background: `linear-gradient(to top, ${GOLD}00, ${GOLD})` }} />
        </div>
      ) : categoryId ? (
        /* ── Category view: collection-led experience ── */
        <CollectionLedCategory
          category={{
            slug:        categoryId,
            label:       catLabel,
            description: catRecord?.description || '',
            heroImage:   catRecord?.hero_image || cover?.cover_image,
          }}
          posts={posts}
          chapterImages={chapterImages}
          collectionsData={collectionsData}
          onArticle={onNavigateArticle}
          // Real hrefs on the article cards. The path shape is the parent's job,
          // not the card's: editorial articles resolve to /lwd-edit/<slug>, the
          // same route main.jsx builds for page "editorial-article".
          //
          // Gated: an unpublished article has no page, so it gets no href. Without
          // this the hub emitted crawlable links to unpublished features (the
          // Capovaticano and Villa Porta Edit posts, both cold prospects whose
          // listings are deliberately draft). The card still renders and still
          // opens in-app; it simply stops being a link a crawler can follow.
          hrefFor={slug => {
            const u = slug ? `/lwd-edit/${slug}` : undefined;
            return u && isPubliclyRoutable(u) ? u : undefined;
          }}
          onNavigateCategory={onNavigateCategory}
          isLight={!isDark}
        />
      ) : (
        <>
          {/* ── Cover Story ── */}
          {cover && (
            <CoverStory post={cover} onNavigateArticle={onNavigateArticle} />
          )}

          {/* ── This Issue, asymmetric feature grid ── */}
          {(feat1 || feat2) && (
            <section style={{
              padding: 'clamp(56px, 7vw, 100px) clamp(24px, 6vw, 80px)',
            }}>
              <SectionHeader label="This Issue" sub={issueLabel} />

              {/* 2-column: left single tall | right stacked pair */}
              <div style={{
                display:             'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap:                 'clamp(24px, 3vw, 56px)',
                alignItems:          'start',
              }}>
                {/* Left, single large feature */}
                {feat1 && (
                  <FeatureCard
                    post={feat1}
                    onNavigateArticle={onNavigateArticle}
                    size="xl"
                    showExcerpt
                  />
                )}

                {/* Right, stacked pair */}
                {(feat2 || feat3) && (
                  <div style={{
                    display:       'flex',
                    flexDirection: 'column',
                    gap:           'clamp(32px, 3.5vw, 56px)',
                  }}>
                    {feat2 && (
                      <FeatureCard
                        post={feat2}
                        onNavigateArticle={onNavigateArticle}
                        size="lg"
                        showExcerpt
                      />
                    )}
                    {feat3 && (
                      <FeatureCard
                        post={feat3}
                        onNavigateArticle={onNavigateArticle}
                        size="md"
                        showExcerpt={false}
                      />
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* ── Gold divider ── */}
          <div style={{
            margin:     '0 clamp(24px, 6vw, 80px)',
            height:     1,
            background: `linear-gradient(to right, transparent, ${GOLD}55, transparent)`,
          }} />

          {/* ── Full-width editorial break ── */}
          {breakA && (
            <FullWidthEditorial
              post={breakA}
              onNavigateArticle={onNavigateArticle}
              sectionLabel="Destinations"
            />
          )}

          {/* ── Also in This Issue, 3-col grid ── */}
          {grid3.length > 0 && (
            <section style={{
              padding: 'clamp(56px, 7vw, 96px) clamp(24px, 6vw, 80px)',
            }}>
              <SectionHeader label="Also in This Issue" />
              <div style={{
                display:             'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap:                 'clamp(24px, 2.5vw, 44px)',
              }}>
                {grid3.map(p => (
                  <FeatureCard
                    key={p.id}
                    post={p}
                    onNavigateArticle={onNavigateArticle}
                    size="md"
                    showExcerpt
                  />
                ))}
              </div>
            </section>
          )}

          {/* ── Dark band divider ── */}
          {latest.length > 0 && (
            <div style={{
              background: C.surface,
              margin:     '0 clamp(24px, 6vw, 80px)',
              height:     1,
            }} />
          )}

          {/* ── Latest Stories, compact 4-up ── */}
          {latest.length > 0 && (
            <section style={{
              padding: 'clamp(56px, 7vw, 96px) clamp(24px, 6vw, 80px)',
            }}>
              <SectionHeader label="Latest Stories" />
              <div style={{
                display:             'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap:                 'clamp(24px, 2.5vw, 36px)',
              }}>
                {latest.map(p => (
                  <CompactCard
                    key={p.id}
                    post={p}
                    onNavigateArticle={onNavigateArticle}
                  />
                ))}
              </div>
            </section>
          )}

          {/* No custom footer here, the global <SiteFooter> from main.jsx
              renders below for all non-dashboard pages and covers copyright,
              nav and social. The masthead's ← Directory + clickable THE LWD EDIT
              wordmark already cover wayfinding. */}
        </>
      )}
    </div>
  );
}
